import React from "react";
import { Partner } from "../../models/Partner";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import ModalContainer from "../ModalContainer.tsx";

const FALLBACK_AVATAR_SRC = "/assets/img/avatar.jpg";
const HERO_STYLE_ID = "profile-partner-hero-styles-v8";

const ProfilePartnerHeroCard = ({
  partner,
  profileUrl,
}: {
  partner: Partner;
  profileUrl: string;
}) => {
  const [showProfileImage, setShowProfileImage] = React.useState(false);
  const [displaySrc, setDisplaySrc] = React.useState(FALLBACK_AVATAR_SRC);

  React.useEffect(() => {
    const trimmed = String(profileUrl || "").trim();
    if (!trimmed) {
      setDisplaySrc(FALLBACK_AVATAR_SRC);
      return;
    }

    let cancelled = false;
    const probe = new Image();
    probe.onload = () => {
      if (!cancelled) setDisplaySrc(trimmed);
    };
    probe.onerror = () => {
      if (!cancelled) setDisplaySrc(FALLBACK_AVATAR_SRC);
    };
    probe.src = trimmed;

    return () => {
      cancelled = true;
      probe.onload = null;
      probe.onerror = null;
    };
  }, [profileUrl]);

  React.useEffect(() => {
    [
      "profile-partner-hero-styles",
      "profile-partner-hero-styles-v2",
      "profile-partner-hero-styles-v3",
      "profile-partner-hero-styles-v4",
      "profile-partner-hero-styles-v5",
      "profile-partner-hero-styles-v6",
      "profile-partner-hero-styles-v7",
    ].forEach((id) => document.getElementById(id)?.remove());
    if (document.getElementById(HERO_STYLE_ID)) return;
    const styleSheet = document.createElement("style");
    styleSheet.id = HERO_STYLE_ID;
    styleSheet.textContent = `
      .profile-hero-card {
        position: relative;
        width: 100%;
        max-width: 100%;
        border-radius: 24px;
        overflow: hidden;
        background: #f1f5f9;
        border: 2px solid #e2e8f0;
        box-shadow:
          0 10px 40px rgba(15, 23, 42, 0.16),
          0 4px 12px rgba(15, 23, 42, 0.08),
          0 0 0 1px rgba(255, 255, 255, 0.6) inset;
        box-sizing: border-box;
      }
      .profile-hero-card__photo-wrap {
        display: block;
        width: 100%;
        aspect-ratio: 4 / 5;
        max-height: 380px;
        cursor: pointer;
        overflow: hidden;
      }
      .profile-hero-card__photo {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center 28%;
      }
    `;
    document.head.appendChild(styleSheet);
  }, []);

  const fullName = toCamelCase(
    `${partner.person.firstName} ${partner.person.lastName} ${partner.person.secondLastName}`
  );

  return (
    <>
      <div className="profile-hero-card">
        <div
          className="profile-hero-card__photo-wrap"
          onClick={() => setShowProfileImage(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setShowProfileImage(true);
          }}
          title="Ver foto de perfil"
        >
          <img src={displaySrc} alt={fullName} className="profile-hero-card__photo" />
        </div>
      </div>

      <ModalContainer
        open={showProfileImage}
        title="Foto de perfil"
        closeFn={() => setShowProfileImage(false)}
      >
        <img
          src={displaySrc}
          alt="Foto de perfil"
          style={{ width: "100%", borderRadius: "8px" }}
        />
      </ModalContainer>
    </>
  );
};

export default ProfilePartnerHeroCard;
